import clsx from 'clsx';
import { FieldWrapper } from './FieldWrapper';
import { useCallback } from 'react';

export const InputField = (props) => {
  const {
    type = 'text',
    label,
    className,
    onChange,
    maxLength,
    caption,
    disabled,
    value,
    registration,
    error,
    required,
    textTransform = 'normal', // default is normal
    startIcon,
    endIcon,
    variant = 'default', // default variant
    contentType = 'normal', // new prop: normal, numeric, fiat
  } = props;

  const variants = {
    default:
      'dark:bg-gray-800 border-gray-200 dark:border-gray-700 focus:ring-blue-500 focus:border-blue-500',
    outlined:
      'border-2 border-blue-500 focus:ring-blue-500 focus:border-blue-500',
    filled:
      'bg-gray-100 border-transparent focus:ring-blue-500 focus:border-blue-500',
    danger:
      'border-red-500 text-red-600 placeholder-red-400 focus:ring-red-500 focus:border-red-500',
  };

  // Format number with commas for fiat type
  const formatFiat = (numStr) => {
    const num = parseFloat(numStr.replace(/,/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleInputChange = useCallback(
    (e) => {
      let newValue = e.target.value;

      if (contentType === 'numeric') {
        // Remove non-digit characters
        newValue = newValue.replace(/\D/g, '');
      } else if (contentType === 'fiat') {
        // Allow only numbers and commas; remove invalid characters
        newValue = newValue.replace(/[^0-9.]/g, '');
        const parts = newValue.split('.');
        if (parts.length > 2) {
          // More than one decimal point is invalid
          return;
        }
        newValue = formatFiat(newValue);
      }

      // Call external onChange handler if exists
      if (onChange) {
        // Create a synthetic event with formatted value
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: newValue,
          },
        };
        onChange(syntheticEvent);
      }
    },
    [contentType, onChange]
  );

  return (
    <FieldWrapper label={label} error={error}>
      <div
        className={clsx(
          'relative flex items-center rounded-md bg-[#F2F2F2]',
          { 'opacity-70 cursor-not-allowed': disabled },
          className
        )}
      >
        {startIcon && (
          <div className="text-[#166BAB] absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {startIcon}
          </div>
        )}
        <input
          type={type}
          maxLength={maxLength}
          value={value}
          disabled={disabled}
          required={required}
          className={clsx(
            `block w-full py-3 h-[50px] px-3 bg-[#F2F2F2] border ${
              error ? 'border-red-400' : 'border-[#F2F2F2]'
            }  rounded-md placeholder-gray-500 dark:text-white focus:outline-none focus:ring-0 sm:text-sm`,
            variants[variant],
            { 'pl-10': startIcon, 'pr-10': endIcon },
            {
              uppercase: textTransform === 'uppercase',
              lowercase: textTransform === 'lowercase',
            },
            className
          )}
          onChange={handleInputChange}
          style={{ textTransform }}
          {...registration}
        />
        {endIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            {endIcon}
          </div>
        )}
      </div>
      {caption && (
        <div className="my-1 text-xs text-gray-500 flex justify-end">
          {caption}
        </div>
      )}
    </FieldWrapper>
  );
};
